import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Grid, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

const CATEGORIES = [
  { id: 'tasks', name: 'Задания', emoji: '✅' },
  { id: 'dating', name: 'Знакомства', emoji: '💕' },
  { id: 'downloads', name: 'Загрузки', emoji: '📥' },
  { id: 'horoscopes', name: 'Гороскопы', emoji: '🔮' },
  { id: 'ai', name: 'ИИ', emoji: '🤖' },
  { id: 'useful', name: 'Полезное', emoji: '💡' },
  { id: 'gaming', name: 'Игры', emoji: '🎮' },
  { id: 'cinema', name: 'Кино', emoji: '🎬' },
  { id: 'kids', name: 'Детское', emoji: '👶' },
  { id: 'celebrities', name: 'Знаменитости', emoji: '⭐' },
  { id: 'other', name: 'Другое', emoji: '📦' },
]

export default function ResourceCategories() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setSelectedCategories(resource.bot_categories || [])
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id)
        ? prev.filter((c) => c !== id)
        : [...prev, id]
    )
  }

  const toggleAll = () => {
    if (selectedCategories.length === CATEGORIES.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(CATEGORIES.map((c) => c.id))
    }
  }

  const allSelected = selectedCategories.length === CATEGORIES.length

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          bot_categories: selectedCategories,
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update categories:', error)
      setError('Ошибка при сохранении категорий')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить категории', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
              <Grid className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Категории ботов</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Toggle All */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4">
            <button
              onClick={() => {
                hapticFeedback('light')
                toggleAll()
              }}
              className="w-full flex items-center justify-between p-4 bg-tg-bg rounded-xl hover:bg-tg-hint/5 transition-colors"
            >
              <div className="text-left">
                <div className="font-medium text-tg-text">Все категории</div>
                <div className="text-xs text-tg-hint mt-0.5">
                  {allSelected ? 'Отменить выбор всех' : 'Выбрать все категории'}
                </div>
              </div>
              <div className={`relative w-12 h-7 rounded-full transition-colors ${
                allSelected ? 'bg-[#059669]' : 'bg-tg-hint/30'
              }`}>
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    allSelected ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Category Grid */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Выберите категории</h2>
            <p className="text-xs text-tg-hint mt-1">
              Выбрано: {selectedCategories.length} из {CATEGORIES.length}
            </p>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((category) => {
                const isSelected = selectedCategories.includes(category.id)
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      hapticFeedback('light')
                      toggleCategory(category.id)
                    }}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-[#5288c1] bg-[#5288c1]/10'
                        : 'border-transparent bg-tg-bg hover:border-tg-hint/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{category.emoji}</span>
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${
                          isSelected ? 'text-[#5288c1]' : 'text-tg-text'
                        }`}>
                          {category.name}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">О категориях</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
